/**
 * Snapshot Growth & Return cards for the Profit & Loss statement.
 *
 * These are as-of-now statistics, not year-indexed series. Lookup never uses
 * the statement table's displayed fiscal-year columns.
 */

const METADATA_KEYS = new Set(['isExpandable', 'setAttributes', 'class']);

export const GROWTH_AND_RETURN_NOT_AVAILABLE = 'Not available';

export const GROWTH_AND_RETURN_CARDS = [
    {
        id: 'compoundedSalesGrowth',
        title: 'Compounded Sales Growth',
        periods: ['10 Years', '5 Years', '3 Years', 'TTM'],
        legacyPeriodKeys: ['10 Years:', '5 Years:', '3 Years:', 'TTM:'],
    },
    {
        id: 'compoundedProfitGrowth',
        title: 'Compounded Profit Growth',
        periods: ['10 Years', '5 Years', '3 Years', 'TTM'],
        legacyPeriodKeys: [],
    },
    {
        id: 'stockPriceCagr',
        title: 'Stock Price CAGR',
        periods: ['10 Years', '5 Years', '3 Years', '1 Year'],
        legacyPeriodKeys: [null, null, null, '1 Year:'],
    },
    {
        id: 'returnOnEquity',
        title: 'Return on Equity',
        periods: ['10 Years', '5 Years', '3 Years', 'Last Year'],
        legacyPeriodKeys: [null, null, null, 'Last Year:'],
    },
];

function isBlank(value) {
    if (value == null) return true;
    return String(value).trim() === '';
}

function scalarText(value) {
    if (value == null) return null;
    if (typeof value === 'object') return null;
    const text = String(value).trim();
    return text || null;
}

/**
 * First non-metadata scalar in a leftover year-keyed object such as `{ "2015": "17%" }`.
 *
 * @param {unknown} obj
 * @returns {string|null}
 */
export function readFirstScalar(obj) {
    const direct = scalarText(obj);
    if (direct) return direct;
    if (!obj || typeof obj !== 'object') return null;

    const entries = Object.entries(obj);
    for (let index = 0; index < entries.length; index += 1) {
        const [key, value] = entries[index];
        if (METADATA_KEYS.has(key)) continue;
        const text = scalarText(value);
        if (text) return text;
    }
    return null;
}

function periodKeyCandidates(period) {
    const trimmed = String(period || '').trim();
    if (!trimmed) return [];
    if (trimmed.endsWith(':')) {
        return [trimmed, trimmed.slice(0, -1)];
    }
    return [trimmed, `${trimmed}:`];
}

function readPeriodFromObject(obj, period) {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
    const candidates = periodKeyCandidates(period);
    for (let index = 0; index < candidates.length; index += 1) {
        const key = candidates[index];
        if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
        const value = obj[key];
        const nested = readFirstScalar(value);
        if (nested) return nested;
    }
    return null;
}

/**
 * Resolve one period line for a card.
 * Prefers structured growthAndReturn[title][period]; falls back to leftover P&L sibling keys.
 *
 * @param {Record<string, unknown>|null|undefined} profitAndLoss
 * @param {Record<string, unknown>|null|undefined} growthAndReturn
 * @param {{ title: string, periods: string[], legacyPeriodKeys: Array<string|null> }} card
 * @param {number} periodIndex
 * @returns {string|null}
 */
export function resolveCardPeriodValue(profitAndLoss, growthAndReturn, card, periodIndex) {
    const period = card.periods[periodIndex];
    const structured = growthAndReturn?.[card.title];
    const fromStructured = readPeriodFromObject(structured, period);
    if (fromStructured) return fromStructured;

    const legacyKey = card.legacyPeriodKeys?.[periodIndex];
    if (legacyKey && profitAndLoss && Object.prototype.hasOwnProperty.call(profitAndLoss, legacyKey)) {
        return readFirstScalar(profitAndLoss[legacyKey]);
    }
    return null;
}

/**
 * @param {Record<string, unknown>|null|undefined} profitAndLoss
 * @param {Record<string, unknown>|null|undefined} growthAndReturn
 * @returns {Array<{
 *   id: string,
 *   title: string,
 *   lines: Array<{ period: string, value: string|null, display: string, available: boolean }>,
 * }>}
 */
export function resolveGrowthAndReturnCards(profitAndLoss, growthAndReturn) {
    return GROWTH_AND_RETURN_CARDS.map((card) => ({
        id: card.id,
        title: card.title,
        lines: card.periods.map((period, periodIndex) => {
            const value = resolveCardPeriodValue(profitAndLoss, growthAndReturn, card, periodIndex);
            const available = !isBlank(value);
            return {
                period: period.endsWith(':') ? period : `${period}:`,
                value,
                display: available ? value : GROWTH_AND_RETURN_NOT_AVAILABLE,
                available,
            };
        }),
    }));
}
